import React, { useEffect, useState } from "react";
import { Box, Grid, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import LandingLayout from "../../../layout/LandingLayout";
import { colors, theme } from "../../../theme/theme";
import { CustomBtn } from "../../../components/Common/CustomBtn";
import DocumentFilePreview from "../../../components/Common/DocumentFilePreview";
import PostViewerModal from "../../../components/Common/PostViewerModal";
import MediaViewerModal from "../../../components/Common/MediaViewerModal";
import DocumentViewerModal from "../../../components/Common/DocumentViewerModal";
import { getAllPosts } from "../../../redux/dispatchers/posts.dispatcher";
import { getAllPhotos } from "../../../redux/dispatchers/gallery.dispatcher";
import { getAllDocuments } from "../../../redux/dispatchers/documents.dispatcher";
import { getAboutMe } from "../../../redux/dispatchers/aboutMe.dispatcher";

const SectionHeader = ({ title, onViewAll }) => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="space-between"
    sx={{ mb: 2 }}
  >
    <Typography
      sx={{
        color: "#fff",
        fontFamily: "Mulish-Bold",
        fontSize: "20px",
      }}
    >
      {title}
    </Typography>
    {onViewAll && (
      <Typography
        onClick={onViewAll}
        sx={{
          color: colors.secondary,
          fontFamily: "Mulish-SemiBold",
          fontSize: "13px",
          cursor: "pointer",
          "&:hover": { textDecoration: "underline" },
        }}
      >
        View All
      </Typography>
    )}
  </Stack>
);

const LandingHome = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { allPosts } = useSelector((state) => state.posts);
  const { allPhotos } = useSelector((state) => state.gallery);
  const { allDocuments } = useSelector((state) => state.documents);
  const { aboutMe } = useSelector((state) => state.aboutMe);

  const [selectedPost, setSelectedPost] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);

  useEffect(() => {
    dispatch(getAllPosts());
    dispatch(getAllPhotos());
    dispatch(getAllDocuments());
    dispatch(getAboutMe());
  }, [dispatch]);

  const posts = allPosts || [];
  const photos = allPhotos || [];
  const documents = allDocuments || [];

  const [latestPost, ...restPosts] = posts;
  const recentPosts = restPosts.slice(0, 3);
  const popularPosts = posts.slice(0, 3);

  const hero = (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: { xs: "360px", md: "460px" },
        backgroundImage: aboutMe?.cover_url ? `url(${aboutMe.cover_url})` : "none",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundColor: colors.primary,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(90deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.35) 100%)",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
          p: { xs: 3, md: 6 },
        }}
      >
        <Typography
          sx={{
            color: colors.secondary,
            fontFamily: "Mulish-SemiBold",
            fontSize: "14px",
            mb: 1,
          }}
        >
          Support Independent Community Journalism
        </Typography>
        <Typography
          sx={{
            color: "#fff",
            fontFamily: "Mulish-ExtraBold",
            fontSize: { xs: "28px", md: "40px" },
            mb: 1,
          }}
        >
          {aboutMe?.name}
        </Typography>
        <Typography
          sx={{
            color: "rgba(255,255,255,0.6)",
            fontFamily: "Mulish-Regular",
            fontSize: "13px",
            mb: 1,
          }}
        >
          {posts.length} posts
        </Typography>
        <Typography
          sx={{
            color: "rgba(255,255,255,0.75)",
            fontFamily: "Mulish-Regular",
            fontSize: "14px",
            mb: 3,
            maxWidth: "520px",
          }}
        >
          {aboutMe?.tagline}
        </Typography>
        <Box>
          <CustomBtn
            text="Sign Up"
            width="180px"
            height="48px"
            onClick={() => navigate("/sign-up")}
          />
        </Box>
      </Box>
    </Box>
  );

  return (
    <LandingLayout hero={hero}>
      {latestPost && (
        <Box sx={{ mt: 4 }}>
          <SectionHeader title="Latest post" />
          <Box
            onClick={() => setSelectedPost(latestPost)}
            sx={{
              borderRadius: "18px",
              overflow: "hidden",
              border: theme.lightBorder,
              cursor: "pointer",
            }}
          >
            <Box
              component="img"
              src={latestPost.images?.[0]?.image_url}
              alt={latestPost.title}
              sx={{
                width: "100%",
                height: { xs: "200px", md: "320px" },
                objectFit: "cover",
                display: "block",
              }}
            />
            <Box sx={{ p: { xs: 2, md: 3 } }}>
              <Typography
                sx={{
                  color: "#fff",
                  fontFamily: "Mulish-Bold",
                  fontSize: "18px",
                  mb: 0.5,
                }}
              >
                {latestPost.title}
              </Typography>
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.6)",
                  fontFamily: "Mulish-Regular",
                  fontSize: "13px",
                }}
              >
                {latestPost.date}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {recentPosts.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <SectionHeader title="Recent posts" onViewAll={() => navigate("/posts")} />
          <Grid container spacing={2}>
            {recentPosts.map((post) => (
              <Grid item size={{ xs: 12, sm: 6, md: 4 }} key={post.id}>
                <Box
                  onClick={() => setSelectedPost(post)}
                  sx={{
                    borderRadius: "14px",
                    overflow: "hidden",
                    border: theme.lightBorder,
                    cursor: "pointer",
                  }}
                >
                  <Box
                    component="img"
                    src={post.images?.[0]?.image_url}
                    alt={post.title}
                    sx={{
                      width: "100%",
                      height: "150px",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                  <Box sx={{ p: 1.5 }}>
                    <Typography
                      sx={{
                        color: "#fff",
                        fontFamily: "Mulish-SemiBold",
                        fontSize: "14px",
                      }}
                    >
                      {post.title}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {popularPosts.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <SectionHeader title="Popular posts" onViewAll={() => navigate("/posts")} />
          <Grid container spacing={2}>
            {popularPosts.map((post) => (
              <Grid item size={{ xs: 12, sm: 6, md: 4 }} key={post.id}>
                <Box
                  onClick={() => setSelectedPost(post)}
                  sx={{
                    borderRadius: "14px",
                    overflow: "hidden",
                    border: theme.lightBorder,
                    cursor: "pointer",
                  }}
                >
                  <Box
                    component="img"
                    src={post.images?.[0]?.image_url}
                    alt={post.title}
                    sx={{
                      width: "100%",
                      height: "150px",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                  <Box sx={{ p: 1.5 }}>
                    <Typography
                      sx={{
                        color: "#fff",
                        fontFamily: "Mulish-SemiBold",
                        fontSize: "14px",
                        mb: 0.5,
                      }}
                    >
                      {post.title}
                    </Typography>
                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.6)",
                        fontFamily: "Mulish-Regular",
                        fontSize: "12px",
                      }}
                    >
                      {post.date}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {photos.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <SectionHeader title="Gallery" onViewAll={() => navigate("/gallery")} />
          <Grid container spacing={2}>
            {photos.slice(0, 4).map((photo) => (
              <Grid item size={{ xs: 6, sm: 4, md: 3 }} key={photo.id}>
                <Box
                  component="img"
                  src={photo.image_url}
                  alt="gallery"
                  onClick={() => setSelectedPhoto(photo)}
                  sx={{
                    width: "100%",
                    height: "150px",
                    objectFit: "cover",
                    borderRadius: "12px",
                    border: theme.lightBorder,
                    display: "block",
                    cursor: "pointer",
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      <Box sx={{ mt: 4, mb: 2 }}>
        <SectionHeader title="Documents" onViewAll={() => navigate("/documents")} />
        {documents.length === 0 ? (
          <Typography
            sx={{
              color: "rgba(255,255,255,0.6)",
              fontFamily: "Mulish-Regular",
              fontSize: "14px",
            }}
          >
            No documents available.
          </Typography>
        ) : (
          <Grid container spacing={2}>
            {documents.slice(0, 4).map((doc) => (
              <Grid item size={{ xs: 12, sm: 6, md: 3 }} key={doc.id}>
                <Box
                  onClick={() => setSelectedDocument(doc)}
                  sx={{
                    borderRadius: "14px",
                    overflow: "hidden",
                    border: theme.lightBorder,
                    cursor: "pointer",
                  }}
                >
                  {doc.cover_url ? (
                    <Box
                      component="img"
                      src={doc.cover_url}
                      alt={doc.title}
                      sx={{
                        width: "100%",
                        height: "110px",
                        objectFit: "cover",
                        display: "block",
                      }}
                    />
                  ) : (
                    <DocumentFilePreview fileName={doc.file_name} height="110px" />
                  )}
                  <Box sx={{ p: 1.5 }}>
                    <Typography
                      sx={{
                        color: "#fff",
                        fontFamily: "Mulish-SemiBold",
                        fontSize: "13px",
                      }}
                    >
                      {doc.title}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      <PostViewerModal
        open={Boolean(selectedPost)}
        onClose={() => setSelectedPost(null)}
        post={selectedPost}
      />

      <MediaViewerModal
        open={Boolean(selectedPhoto)}
        onClose={() => setSelectedPhoto(null)}
        src={selectedPhoto?.image_url}
        title="Photo"
        rotatable
      />

      <DocumentViewerModal
        open={Boolean(selectedDocument)}
        onClose={() => setSelectedDocument(null)}
        document={selectedDocument}
      />
    </LandingLayout>
  );
};

export default LandingHome;
